import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AgiInput, AgiKind, AgiScope, FilingStatus } from 'api/StudentLoanApi';
import FormNavigation from 'components/FormNavigation';
import RadioButtonList from 'components/Common/RadioButtonList/RadioButtonList';
import NumberInput from 'components/NumberInput';
import Button from 'components/Button';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import { formatRoundedAmount, parseAmount, toInputValue } from 'components/StudentLoanForgiveness/studentLoanFormat';
import useSaveStudentLoanAnswers, { reportStudentLoanSaveFailure } from 'hooks/useSaveStudentLoanAnswers';
import { FlowComponentType } from 'routes/types';
import { getApplicationData } from 'selectors/getApplicationData';
import { getStudentLoanData } from 'selectors/getStudentLoanData';

import QuestionsWrapper from './Wrapper/QuestionsWrapper';

import styles from './Questions.module.scss';

const MONTHS_PER_YEAR = 12;
const PERCENT_MAX = 100;

const scopeOptions = [
  { id: AgiScope.Individual, label: 'This is my own AGI' },
  { id: AgiScope.Joint, label: 'This is from a joint return, so it already includes my spouse' },
];

const Income = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const { answers } = useSelector(getStudentLoanData);
  const { application } = useSelector(getApplicationData);
  const { saveAnswers, isSaving } = useSaveStudentLoanAnswers();

  const savedEstimate = answers.agi?.kind === AgiKind.Estimate ? answers.agi : undefined;
  const savedKnown = answers.agi?.kind === AgiKind.Known ? answers.agi : undefined;

  const [isKnown, setIsKnown] = useState(Boolean(savedKnown));
  const [salary, setSalary] = useState(toInputValue(savedEstimate?.salary ?? application?.totalAnnualIncome));
  const [retirementPct, setRetirementPct] = useState(toInputValue(savedEstimate?.retirementPct));
  const [premiums, setPremiums] = useState(toInputValue(savedEstimate?.pretaxPremiumsMonthly));
  const [hsaFsa, setHsaFsa] = useState(toInputValue(savedEstimate?.hsaFsaAnnual));
  const [otherIncome, setOtherIncome] = useState(toInputValue(savedEstimate?.otherIncomeAnnual));
  const [knownAgi, setKnownAgi] = useState(toInputValue(savedKnown?.agi));
  const [scope, setScope] = useState<AgiScope>(savedKnown?.scope ?? AgiScope.Individual);

  const isJointFiling = answers.filingStatus === FilingStatus.MarriedFilingJointly;

  // The application loads alongside this screen on a fresh visit, so the salary may arrive after the first render.
  useEffect(() => {
    if (!salary && application?.totalAnnualIncome) {
      setSalary(toInputValue(application.totalAnnualIncome));
    }
  }, [application?.totalAnnualIncome]);

  const estimatedAgi = Math.max(
    0,
    parseAmount(salary) -
      (parseAmount(salary) * parseAmount(retirementPct)) / PERCENT_MAX -
      parseAmount(premiums) * MONTHS_PER_YEAR -
      parseAmount(hsaFsa) +
      parseAmount(otherIncome),
  );

  const hasEnteredAmount = (value: string) => value.trim() !== '';
  // Zero is a real AGI. An empty field is not.
  const canContinue = isKnown ? hasEnteredAmount(knownAgi) : hasEnteredAmount(salary);

  const buildAgi = (): AgiInput =>
    isKnown
      ? { kind: AgiKind.Known, agi: parseAmount(knownAgi), scope: isJointFiling ? scope : AgiScope.Individual }
      : {
          kind: AgiKind.Estimate,
          salary: parseAmount(salary),
          retirementPct: parseAmount(retirementPct),
          pretaxPremiumsMonthly: parseAmount(premiums),
          hsaFsaAnnual: parseAmount(hsaFsa),
          otherIncomeAnnual: parseAmount(otherIncome),
        };

  const handleClick = async () => {
    const agi = buildAgi();
    analytics.track('Student Loan Income Selected', { kind: agi.kind });
    try {
      handleNext(await saveAnswers({ agi }));
    } catch {
      reportStudentLoanSaveFailure();
    }
  };

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        <QuestionsWrapper
          title="Let's estimate your AGI"
          subtitle="Your payment is based on adjusted gross income, which is usually lower than your salary."
        >
          <>
            {isKnown ? (
              <div className={styles.fields}>
                <NumberInput
                  label="Enter line 11 (adjusted gross income) from your Form 1040"
                  placeholder="$0"
                  name="knownAgi"
                  prefix="$"
                  thousandSeparator
                  allowNegative={false}
                  value={knownAgi}
                  onChange={(event) => setKnownAgi(event.target.value)}
                />
                {isJointFiling && (
                  <RadioButtonList
                    label="Whose income does this AGI include?"
                    selected={scope}
                    setSelected={setScope}
                    options={scopeOptions}
                  />
                )}
                <button type="button" className={styles.linkButton} onClick={() => setIsKnown(false)}>
                  Estimate it for me instead
                </button>
              </div>
            ) : (
              <div className={styles.fields}>
                <NumberInput
                  label="Annual salary (from your application)"
                  placeholder="$0"
                  name="salary"
                  prefix="$"
                  thousandSeparator
                  allowNegative={false}
                  value={salary}
                  onChange={(event) => setSalary(event.target.value)}
                />
                <NumberInput
                  label="Pre-tax 403(b) or 457 contributions (% of pay)"
                  placeholder="0%"
                  name="retirementPct"
                  suffix="%"
                  allowNegative={false}
                  isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= PERCENT_MAX}
                  value={retirementPct}
                  onChange={(event) => setRetirementPct(event.target.value)}
                />
                <NumberInput
                  label="Pre-tax health, dental and vision premiums (per month)"
                  placeholder="$0"
                  name="pretaxPremiumsMonthly"
                  prefix="$"
                  thousandSeparator
                  allowNegative={false}
                  value={premiums}
                  onChange={(event) => setPremiums(event.target.value)}
                />
                <NumberInput
                  label="HSA or FSA contributions (per year)"
                  placeholder="$0"
                  name="hsaFsaAnnual"
                  prefix="$"
                  thousandSeparator
                  allowNegative={false}
                  value={hsaFsa}
                  onChange={(event) => setHsaFsa(event.target.value)}
                />
                <NumberInput
                  label="Other income, such as a second job (per year)"
                  placeholder="$0"
                  name="otherIncomeAnnual"
                  prefix="$"
                  thousandSeparator
                  allowNegative={false}
                  value={otherIncome}
                  onChange={(event) => setOtherIncome(event.target.value)}
                />
                <div className={styles.estimatedAgi}>
                  <span>Estimated AGI</span>
                  <span className={styles.amount}>{formatRoundedAmount(estimatedAgi)}</span>
                </div>
                <button type="button" className={styles.linkButton} onClick={() => setIsKnown(true)}>
                  Enter AGI from tax return
                </button>
              </div>
            )}

            <Button disabled={!canContinue} isLoading={isSaving} onClick={handleClick}>
              Next
            </Button>
          </>
        </QuestionsWrapper>
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default Income;
